import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { semantic_colors, Typography } from '../../foundation';
import { Popover, Stack } from '../../components';
import { IconCircleQuestion } from '../../components/icons/generated';
import { BaseText } from '../../components/text';
import { CSSValueWithLength } from '../../styles';

export type SectionTitleDirection = 'horizontal' | 'vertical';
export type SectionTitleSize = 'small' | 'medium' | 'large';

export interface SectionTitleProps {
  /** @default horizontal */
  direction?: SectionTitleDirection;
  /** @default large */
  size?: SectionTitleSize;
  caption?: string;
  title: ReactNode;
  tooltip?: ReactNode;
  description?: ReactNode;
  textButton?: ReactNode;
  actions?: ReactNode;
  mb?: CSSValueWithLength;
}

const text_kind = {
  caption: {
    small: 'Caption_11_Medium',
    medium: 'Body_12_Medium',
    large: 'Body_13_Medium',
  },
  title: {
    small: 'Body_15_Bold',
    medium: 'Heading_17_Bold',
    large: 'Heading_24_Bold',
  },
  description: {
    small: 'Body_12_Regular',
    medium: 'Body_13_Regular',
    large: 'Body_14_Regular',
  },
};

const tooltip_icon_size: Record<SectionTitleSize, number> = {
  small: 14,
  medium: 16,
  large: 20,
};

const margin_bottom_size = {
  small: 16,
  medium: 24,
  large: 32,
};

export const SectionTitle = ({
  direction = 'horizontal',
  size = 'large',
  caption,
  title,
  tooltip,
  description,
  textButton,
  actions,
  mb,
}: SectionTitleProps) => {
  const default_spacing = size === 'small' ? 4 : 8;
  const default_margin_bottom = mb ?? margin_bottom_size[size];
  const actions_gap = direction === 'vertical' ? 24 : 0;

  return (
    <Stack
      direction={direction === 'vertical' ? 'column' : undefined}
      align={direction === 'vertical' ? 'stretch' : 'flex-end'}
      mb={default_margin_bottom}
      gap={actions_gap}
    >
      <Stack direction='column' gap={default_spacing} flex={1} width={direction === 'vertical' ? '100%' : undefined}>
        {caption && (
          <BaseText typography={text_kind.caption[size] as Typography} color={semantic_colors.content.secondary}>
            {caption}
          </BaseText>
        )}
        <Stack width='100%' align='center' gap={4}>
          <BaseText as='h1' typography={text_kind.title[size] as Typography} color={semantic_colors.content.primary}>
            {title}
          </BaseText>
          {tooltip && (
            <Popover placement='top-start' description={tooltip}>
              <TooltipOpener>
                <IconCircleQuestion size={tooltip_icon_size[size]} color={semantic_colors.content.tertiary} />
              </TooltipOpener>
            </Popover>
          )}
          {textButton && (
            <Stack flex={1} align={direction === 'vertical' ? 'flex-start' : 'center'} gap={8} pl={12}>
              {textButton}
            </Stack>
          )}
        </Stack>
        {description && (
          <BaseText typography={text_kind.description[size] as Typography} color={semantic_colors.content.secondary}>
            {description}
          </BaseText>
        )}
      </Stack>
      {actions && (
        <Stack
          ml={direction === 'horizontal' ? 'auto' : undefined}
          align='flex-end'
          width={direction === 'vertical' ? '100%' : undefined}
        >
          {actions}
        </Stack>
      )}
    </Stack>
  );
};

const TooltipOpener = styled.div`
  display: flex;
  align-items: center;
`;
